/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { DatePickerModule } from "primeng/datepicker";
import { MessageModule } from "primeng/message";
import { MessageService } from "primeng/api";
import { catchError, of, Subject, switchMap, takeUntil, tap } from "rxjs";

import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { TimezoneHelper } from "../../../../core/utils/timezone.helper";
import {
  RescheduleDialogData,
  RescheduleDialogResult,
} from "../../interfaces/reschedule-dialog-data.interface";

/**
 * Reschedule Dialog Component
 * Allows changing the NextOccurrenceUtc date of a scheduled order
 * Matches Portal V2 reschedule-order functionality
 *
 * Supports dual mode:
 * - Standard mode: Uses SessionService for authentication
 * - Embedded mode: Uses inputToken passed via config.data
 */
@Component({
  selector: "app-reschedule-dialog",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DatePickerModule,
    MessageModule,
  ],
  templateUrl: "./reschedule-dialog.component.html",
  styleUrl: "./reschedule-dialog.component.scss",
})
export class RescheduleDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Dialog data
  orderId!: number;
  siteId!: number;
  currentNextOccurrenceUtc?: Date;
  utcOffset: number = 0;
  resumeAfter: boolean = false;
  inputToken?: QPilotAccessToken;

  // Form state
  selectedDate: Date | null = null;
  minDate: Date = new Date();
  isLoading: boolean = false;
  isMobile: boolean = false;

  private timezoneHelper = new TimezoneHelper();

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    const data = this.config.data as RescheduleDialogData & {
      siteId: number;
      inputToken?: QPilotAccessToken;
    };

    this.orderId = data.orderId;
    this.siteId = data.siteId;
    this.currentNextOccurrenceUtc = data.currentNextOccurrenceUtc;
    this.utcOffset = data.utcOffset ?? 0;
    this.resumeAfter = data.resumeAfter ?? false;
    this.inputToken = data.inputToken;

    // Initialize selected date from current value
    if (this.currentNextOccurrenceUtc) {
      this.selectedDate = new Date(this.currentNextOccurrenceUtc);
    }

    // Set minimum date to tomorrow (can't schedule in the past)
    this.minDate = new Date();
    this.minDate.setDate(this.minDate.getDate() + 1);
    this.minDate.setHours(0, 0, 0, 0);

    // Detect mobile for touchUI mode
    this.isMobile = window.innerWidth < 768;
  }

  /**
   * Get access token based on mode
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  /**
   * Confirm and update the next occurrence date
   */
  confirm(): void {
    if (!this.selectedDate || !this.canConfirm()) return;

    const accessToken = this.getAccessToken();
    if (!accessToken) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Authentication error. Please refresh the page.",
      });
      return;
    }

    this.isLoading = true;

    // Call API to update next occurrence
    this.qpilotService
      .updateScheduledOrderNextOccurrence(
        this.siteId,
        this.orderId,
        this.selectedDate,
        accessToken,
        false,
      )
      .pipe(
        switchMap((order) => {
          // If resumeAfter is true, also activate the order (for paused orders)
          if (this.resumeAfter) {
            return this.qpilotService
              .activateScheduledOrder(this.siteId, this.orderId, accessToken)
              .pipe(
                tap(() => console.log("Order reactivated after reschedule")),
                // Return original order on success
                switchMap(() => of(order)),
              );
          }
          return of(order);
        }),
        catchError((error) => {
          console.error("Failed to reschedule order:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to update next order date. Please try again.",
          });
          this.isLoading = false;
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        this.isLoading = false;
        if (result) {
          const dialogResult: RescheduleDialogResult = {
            success: true,
            newNextOccurrenceUtc: this.selectedDate!,
          };
          this.ref.close(dialogResult);
        }
      });
  }

  /**
   * Cancel and close dialog without changes
   */
  cancel(): void {
    this.ref.close(undefined);
  }

  /**
   * Check if confirm button should be enabled
   */
  canConfirm(): boolean {
    if (!this.selectedDate) return false;

    // Must be in the future
    const now = new Date();
    return this.selectedDate > now;
  }

  /**
   * Format date for display with site timezone
   * Uses TimezoneHelper to match table display
   */
  formatCurrentDate(): string {
    if (!this.currentNextOccurrenceUtc) return "Not set";
    console.log(
      "Formatting date:",
      this.currentNextOccurrenceUtc,
      "with offset",
      this.utcOffset,
    );
    return this.timezoneHelper.formatDateWithTimezone(
      this.currentNextOccurrenceUtc,
      this.utcOffset,
      {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
